import appConfig from '@/features/app/constants/app-config';
import captureSentryException from '@/features/app/functions/captureSentryException';
import getFirebaseMessaging from '@/features/push/functions/get-firebase-messaging';
import type PushTokenProvider from '@/features/push/types/push-token-provider';
import { deleteToken, getToken } from 'firebase/messaging';

const webPushTokenProvider: PushTokenProvider = {
  /**
   * Get the FCM token for the current device.
   * @returns The FCM token and device type.
   */
  async getToken() {
    if (window.ReactNativeWebView) return { fcmToken: null, deviceType: 'web' };

    const messaging = await getFirebaseMessaging();
    const vapidKey = appConfig.firebase.vapidKey;

    if (!messaging || !vapidKey) return { fcmToken: null, deviceType: 'web' };
    if (!('Notification' in window) || navigator.serviceWorker === undefined)
      return { fcmToken: null, deviceType: 'web' };
    if (Notification.permission === 'denied') return { fcmToken: null, deviceType: 'web' };

    const { serviceWorker } = navigator;
    try {
      const registration = await serviceWorker.ready;
      const fcmToken = await getToken(messaging, {
        vapidKey,
        serviceWorkerRegistration: registration,
      });
      return { fcmToken: fcmToken || null, deviceType: 'web' };
    } catch (error) {
      captureSentryException(error, {
        tags: { feature: 'push.getToken' },
      });
      return { fcmToken: null, deviceType: 'web' };
    }
  },
  /**
   * Clear the FCM token for the current device.
   */
  async clearToken() {
    if (window.ReactNativeWebView) return;

    const messaging = await getFirebaseMessaging();
    if (!messaging) return;

    try {
      await deleteToken(messaging);
    } catch (error) {
      captureSentryException(error, {
        tags: { feature: 'push.clearToken' },
      });
    }
  },
};

export default webPushTokenProvider;
